import * as Sentry from "@sentry/react";
import { xor } from "lodash";
import { axiosPatch, axiosPost } from "src/api/axiosCalls";

import { createSlice } from "@reduxjs/toolkit";
import { kebabCaseKeys } from "@utility/utilityFunctions";
import permissions from "@utils/permissions";

import { mapchannelUsers } from "../channelSlice";
import { setError } from "../errorSlice";
import {
  setFailure as patchFailure,
  setIsLoading as patchLoading,
  patchSuccess,
} from "../patchOrderSlice";
import { buildFavoriteItemPatch } from "./helpers";
import { mapUser } from "./maps";

const fieldMap = {
  "has-viewed-dashboard": "hasViewedDashboard",
  "has-viewed-address-book": "hasViewedAddressBook",
  "has-viewed-pre-order": "hasViewedPreOrder",
  "has-viewed-order-set": "hasViewedOrderSet",
};

const initialState = {
  loginIsLoading: false,
  isLoading: false,
  isUpdateLoading: false,
  loggedIn: false,
  id: "",
  firstName: "",
  lastName: "",
  name: "",
  initials: "",
  email: "",
  role: "",
  status: "",
  channels: [],
  channelUsers: [],
  currentChannelId: "",
  territories: [],
  currentTerritoryId: "",
  defaultTerritoryId: null,
  groups: [],
  favoriteItems: [],
  favoriteItemIds: [],
  budgets: [],
  hasViewedDashboard: false,
  hasViewedAddressBook: false,
  hasViewedPreOrder: false,
  hasViewedOrderSet: false,
  invalidConfiguration: false,
  organization: {
    id: null,
    name: null,
    logoUrl: null,
    logoCloudinaryId: null,
    usesApprovalFlow: null,
    usesApprovalThreshold: null,
    usesChannels: null,
    approvalThreshold: null,
    usesOnDemand: null,
    usesInventory: null,
    preferProductGridView: null,
    usesItemCustomization: null,
    budgetLocation: null,
    budgetCategoryId: null,
    includeShippingInBudgets: null,
    includeTaxesInBudgets: null,
    estimatedShippingPercentage: 0.18,
    minimumShippingCost: 9,
    addressBookType: null,
    usesStripe: null,
    stripeAccount: null,
    includeShippingInStripeCharge: null,
    includeTaxesInStripeCharge: null,
    allocationLocation: null,
    orderLimitType: null,
    countries: null,
    warehouses: [],
    approvalFlows: [],
    hasPurchasingAgents: false,
  },
  supplier: null,
  hasFetchedOrgData: false,
  refetchData: false,
  loginError: null,
};

const currentUserSlice = createSlice({
  name: "currentUser",
  initialState,
  reducers: {
    setIsLoading(state) {
      state.isLoading = true;
    },
    setLoginLoading(state) {
      state.loginIsLoading = true;
    },
    setIsUpdateLoading(state) {
      state.isUpdateLoading = true;
    },
    getUserSuccess(state, action) {
      const { user, organization } = action.payload;

      state = Object.assign(state, user);
      state.organization = organization;

      const isValidChannelId = user.channels.some(
        (channel) => channel.id === user.defaultChannelId
      );

      state.currentChannelId = isValidChannelId
        ? user.defaultChannelId
        : user.channels[0]?.id || null;

      const isValidTerritoryId = user.territories.some(
        (territory) => territory.id === user.defaultTerritoryId
      );

      state.currentTerritoryId = isValidTerritoryId
        ? user.defaultTerritoryId
        : user.territories[0]?.id || null;

      // Check territories and channels for internal users
      if (
        permissions.internalRoles.includes(user.role) &&
        user.role !== "super" &&
        ((state.organization.usesChannels && !user.channels.length) ||
          !user.territories.length)
      ) {
        state.invalidConfiguration = true;
      }

      state.isLoading = false;
      state.loggedIn = true;
      state.loginError = null;
    },
    updateUserSuccess(state, action) {
      const { user } = action.payload;
      state = Object.assign(state, user);
      state.isLoading = false;
    },
    updateFavoriteItemsSuccess(state, action) {
      const { items } = action.payload;
      state.favoriteItems = [...items];
      state.isUpdateLoading = false;
    },
    updateCurrentTerritory(state, action) {
      const { territory } = action.payload;
      if (state.territories.some((t) => t.id === territory)) {
        state.currentTerritoryId = territory;
      }
    },
    updateCurrentChannel(state, action) {
      const { channel } = action.payload;
      if (state.channels.some((c) => c.isActive && c.id === channel)) {
        state.currentChannelId = channel;
      }
    },
    updateHasViewedSuccess(state, action) {
      const { field } = action.payload;
      state[fieldMap[field]] = true;
      state.isUpdateLoading = false;
    },
    setHasFetchedData(state, action) {
      const { value } = action.payload;
      state.hasFetchedOrgData = value;
    },
    removeUser: (state) => {
      Object.assign(state, initialState);

      // Clears user tag from Sentry
      Sentry.setUser(null);
    },
    setRefetchData(state, action) {
      const { value } = action.payload;
      state.refetchData = value;
    },
    setLogInFailure(state, action) {
      const { error } = action.payload;
      state.loginIsLoading = false;
      state.loginError = error;
    },
    setFailure(state) {
      state.isLoading = false;
      state.loginIsLoading = false;
      state.isUpdateLoading = false;
    },
    updateChannelUserSuccess(state, action) {
      const newUc = action.payload;
      state.channelUsers = state.channelUsers.map((uc) =>
        uc.id === newUc.id ? newUc : uc
      );
      state.isLoading = false;
    },
  },
});

export const {
  setIsLoading,
  setLoginLoading,
  setIsUpdateLoading,
  getUserSuccess,
  updateUserSuccess,
  updateFavoriteItemsSuccess,
  setRedirectLink,
  updateCurrentTerritory,
  updateCurrentChannel,
  updateHasViewedSuccess,
  removeUser,
  setHasFetchedData,
  setRefetchData,
  setLogInFailure,
  setUpdateFailure,
  setFailure,
  updateChannelUserSuccess,
} = currentUserSlice.actions;

export default currentUserSlice.reducer;

export const updateCurrentUser =
  (attributes, maybeCallback) => async (dispatch, getState) => {
    try {
      dispatch(setIsUpdateLoading());
      const response = await axiosPatch("/api/current-user", {
        data: {
          attributes: {
            "organization-id": getState().currentUser.organization.id,
            ...kebabCaseKeys(attributes),
          },
        },
      });
      if (response.error) {
        throw response.error;
      }

      const mappedData = mapUser(response.data);

      dispatch(updateUserSuccess({ user: mappedData }));
    } catch (err) {
      dispatch(setFailure());
      dispatch(
        setError({ error: err.toString(), source: "Update current user" })
      );
    } finally {
      maybeCallback?.();
    }
  };

export const updateHasViewed = (field) => async (dispatch) => {
  try {
    dispatch(setIsUpdateLoading());
    const postData = {
      data: {
        attributes: {
          [field]: true,
        },
      },
    };
    const response = await axiosPost(
      "/api/current-user/update-has-viewed",
      postData
    );
    if (response.error) throw response.error;
    dispatch(updateHasViewedSuccess({ field: field }));
  } catch (err) {
    dispatch(setFailure());
    dispatch(setError({ error: err.toString(), source: "Favorite Items" }));
  }
};

export const updateFavoriteItems = (itemIds) => async (dispatch) => {
  try {
    dispatch(patchLoading());
    dispatch(setIsUpdateLoading());
    const patchData = buildFavoriteItemPatch(itemIds.map(Number));
    const response = await axiosPatch("/api/current-user", patchData);
    if (response.error) {
      throw response.error;
    }
    const mappedData = mapUser(response.data);
    dispatch(updateUserSuccess({ user: mappedData }));
    dispatch(patchSuccess());
  } catch (err) {
    dispatch(setFailure());
    dispatch(setError({ error: err.toString(), source: "Favorite Items" }));
    dispatch(patchFailure());
  }
};

export const toggleFavoriteItem = (itemId) => async (dispatch, getState) => {
  try {
    const { favoriteItemIds, ...user } = getState().currentUser;
    const newFavoriteItemIds = xor(favoriteItemIds, [+itemId]);
    dispatch(
      updateUserSuccess({ user: { user, favoriteItemIds: newFavoriteItemIds } })
    );
    dispatch(patchLoading());
    dispatch(setIsUpdateLoading());
    const patchData = buildFavoriteItemPatch(newFavoriteItemIds);
    const response = await axiosPatch("/api/current-user", patchData);
    if (response.error) {
      dispatch(updateUserSuccess({ user: { user, favoriteItemIds } }));
      throw response.error;
    }
    const mappedData = mapUser(response.data);
    dispatch(updateUserSuccess({ user: mappedData }));
    dispatch(patchSuccess());
  } catch (err) {
    dispatch(setFailure());
    dispatch(setError({ error: err.toString(), source: "Favorite Items" }));
    dispatch(patchFailure());
  }
};

export const updateChannelUser =
  (id, attributes, maybeCallback) => async (dispatch) => {
    try {
      const res = await axiosPatch(`/api/channel-users/${id}`, {
        data: { attributes: kebabCaseKeys(attributes) },
      });
      if (res.error) throw res.error;
      dispatch(updateChannelUserSuccess(mapchannelUsers([res.data])[0]));
      dispatch(patchSuccess());
    } catch (error) {
      dispatch(setFailure());
      dispatch(
        setError({ error: error.toString(), source: "Update User Channel" })
      );
      dispatch(patchFailure());
    } finally {
      maybeCallback?.();
    }
  };
